import {
  CURRENCY_SUB_TYPE,
  CURRENCY_SYMBOL,
  PERCENTAGE_FROM_FRACTION_MULTIPLIER,
  PERCENTAGE_FROM_FRACTION_SUB_TYPE,
  PERCENTAGE_SUB_TYPES,
  PERCENT_SYMBOL,
  SCALED_FILTER_VALUE_SIGNIFICANT_DIGITS,
} from "../types/constants";
import { abbreviated } from "common/numbers";

const PLAIN_DATA_TYPES = new Set(["year"]);

/** Returns the prefix and suffix shown around a number value for a column sub-type. */
export function subTypeValueAffixes(subType) {
  if (subType === CURRENCY_SUB_TYPE) {
    return { prefix: CURRENCY_SYMBOL, suffix: "" };
  }
  if (PERCENTAGE_SUB_TYPES.includes(subType)) {
    return { prefix: "", suffix: PERCENT_SYMBOL };
  }
  return { prefix: "", suffix: "" };
}

/** Returns the affixes for a value, or empty affixes when the value is blank. */
export function affixesForValue(value, affixes) {
  return value === null || value === undefined
    ? { prefix: "", suffix: "" }
    : affixes;
}

/** Returns display, exact and short formatters for filter values, each showing the value times multiplier between prefix and suffix. */
export function createNumberFormatter(
  dataType,
  multiplier = 1,
  { prefix = "", suffix = "" } = {}
) {
  const isPlain = PLAIN_DATA_TYPES.has(dataType);
  const affixed = (text) => `${prefix}${text}${suffix}`;

  const display = (value, placeholder = "—") => {
    if (value == null) return placeholder;
    const shown = scaleFilterValue(value, multiplier);
    return affixed(isPlain ? String(shown) : shown.toLocaleString());
  };

  const exact = (value) => {
    if (value == null) return "";
    const shown = scaleFilterValue(value, multiplier);
    return affixed(
      isPlain ? String(shown) : new Intl.NumberFormat().format(shown)
    );
  };

  const short = (value) => {
    if (value == null) return null;
    const shown = scaleFilterValue(value, multiplier);
    return affixed(isPlain ? String(shown) : abbreviated(shown));
  };

  return {
    display,
    exact,
    short,
    inputFormatter: isPlain ? (num) => String(num) : undefined,
    isPlain,
  };
}

/** Returns a fraction scaled to a percentage, or the value unchanged when blank or not numeric. */
export function percentageFromFractionValue(value) {
  if (value === undefined || value === null || value === "") return value;
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num * PERCENTAGE_FROM_FRACTION_MULTIPLIER;
}

/** Returns the factor a column's filter values are shown at relative to the values the backend filters on. */
export function filterValueMultiplier(subType) {
  return subType === PERCENTAGE_FROM_FRACTION_SUB_TYPE
    ? PERCENTAGE_FROM_FRACTION_MULTIPLIER
    : 1;
}

const roundScaled = (num) =>
  Number(num.toPrecision(SCALED_FILTER_VALUE_SIGNIFICANT_DIGITS));

/** Returns a filter value multiplied by multiplier, or the value unchanged when blank. */
export function scaleFilterValue(value, multiplier) {
  if (value === null || value === undefined || multiplier === 1) return value;
  return roundScaled(value * multiplier);
}

/** Returns a filter value divided by multiplier, or the value unchanged when blank. */
export function unscaleFilterValue(value, multiplier) {
  if (value === null || value === undefined || multiplier === 1) return value;
  return roundScaled(value / multiplier);
}

/** Returns a number range with its min and max multiplied by multiplier. */
export function scaleNumberRange(range, multiplier) {
  if (!range || multiplier === 1) return range;
  return {
    ...range,
    min: scaleFilterValue(range.min, multiplier),
    max: scaleFilterValue(range.max, multiplier),
  };
}

/** Returns a number range with its min and max divided by multiplier. */
export function unscaleNumberRange(range, multiplier) {
  if (!range || multiplier === 1) return range;
  return {
    ...range,
    min: unscaleFilterValue(range.min, multiplier),
    max: unscaleFilterValue(range.max, multiplier),
  };
}
